import { Link } from "react-router-dom";
import {
  Phone, EnvelopeSimple, MapPin,
  FacebookLogo, InstagramLogo, YoutubeLogo,
} from "@phosphor-icons/react";
import { SITE, NAV_LINKS } from "@/lib/site";
import { PRODUCTS, localizeProduct } from "@/lib/products";
import { useLang } from "@/lib/i18n";

const SOCIAL_LINKS = [
  { key: "instagram", href: SITE.social.instagram, Icon: InstagramLogo, label: "Instagram" },
  { key: "youtube", href: SITE.social.youtube, Icon: YoutubeLogo, label: "YouTube" },
  { key: "facebook", href: SITE.social.facebook, Icon: FacebookLogo, label: "Facebook" },
];

export const Footer = () => {
  const { t, lang } = useLang();
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-hairline bg-champagne text-ink" data-testid="site-footer">
      <div className="container-x grid grid-cols-1 gap-12 py-16 md:grid-cols-2 lg:grid-cols-4 lg:py-20">
        <div>
          <img
            src={SITE.logo}
            alt="RK Jain"
            className="h-16 w-auto object-contain md:h-20"
          />
          <p className="mt-6 max-w-xs text-sm leading-relaxed text-subtle">{t("footer.desc")}</p>
          <div className="mt-6 flex gap-3">
            {SOCIAL_LINKS.map(({ key, href, Icon, label }) => (
              <a
                key={key}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-hairline bg-surface text-forest transition-colors hover:bg-forest hover:text-sand"
                data-testid={`footer-social-${key}`}
                aria-label={label}
              >
                <Icon size={18} weight="fill" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-heading text-base font-bold text-forest">{t("footer.quickLinks")}</h4>
          <ul className="mt-6 space-y-3 text-sm text-subtle">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="transition-colors hover:text-forest">
                  {t(`nav.${l.key}`)}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-heading text-base font-bold text-forest">{t("footer.products")}</h4>
          <ul className="mt-6 space-y-3 text-sm text-subtle">
            {PRODUCTS.slice(0, 8).map((p) => (
              <li key={p.slug}>
                <Link to={`/products/${p.slug}`} className="transition-colors hover:text-forest">
                  {localizeProduct(p, lang).name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-heading text-base font-bold text-forest">{t("footer.contact")}</h4>
          <ul className="mt-6 space-y-4 text-sm text-subtle">
            <li className="flex items-start gap-3">
              <Phone size={18} className="mt-0.5 shrink-0 text-gold" weight="fill" />
              <div className="flex flex-col">
                <span className="mb-1 text-center text-sm font-bold text-forest" data-testid="footer-marketed-by">
                  All India Marketed By New Pritam Agency
                </span>
                <a href={`tel:${SITE.phoneRaw}`} className="hover:text-forest" data-testid="footer-phone-1">{SITE.phoneDisplay}</a>
                <a href={`tel:${SITE.phoneRawAlt}`} className="hover:text-forest" data-testid="footer-phone-2">{SITE.phoneDisplayAlt}</a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <EnvelopeSimple size={18} className="mt-0.5 shrink-0 text-gold" weight="fill" />
              <div className="flex flex-col">
                <a href={`mailto:${SITE.email}`} className="break-all hover:text-forest" data-testid="footer-email-1">{SITE.email}</a>
                <a href={`mailto:${SITE.emailAlt}`} className="break-all hover:text-forest" data-testid="footer-email-2">{SITE.emailAlt}</a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={18} className="mt-0.5 shrink-0 text-gold" weight="fill" />
              <span>{SITE.addressLines.join(", ")}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-hairline">
        <div className="container-x flex flex-col items-center justify-between gap-3 py-6 text-xs text-subtle md:flex-row">
          <p>{t("footer.rights", { year })}</p>
          <div className="flex gap-6">
            <a href="#" onClick={(e) => e.preventDefault()} className="hover:text-forest">
              {t("footer.privacy")}
            </a>
            <span>{t("footer.crafted")}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
